import { ModulesResource, ProcessElementsResource } from "core/stages-client";
import { ViewService } from "core/view.service";
import { Injectable, inject } from "@angular/core";
import { DialogService } from "common/dialog/dialog.service";
import { MutexService } from "common/concurrency/mutex.service";

type EditableElement = stages.process.EditableElement;
type EditableProcess = stages.process.EditableProcess;
type EditableAttribute = stages.process.EditableAttribute;
type Attribute = stages.core.Attribute;
type TypedViewable = stages.process.TypedViewable;
type ViewableSourceElement = stages.process.ViewableSourceElement;
type ViewableElement = stages.process.ViewableElement;
type ViewableType = stages.process.ViewableType;

export type AttributeScope = "ALL" | "PROCESS" | "PROJECT";

export interface DeletableElement {
	type: stages.process.ViewableType;
	id: string;
}

export interface SingleDeletableElement extends DeletableElement {
	label: string; //confirm dialog
}

export interface OverwriteableElement {
	type: stages.process.ViewableType;
	id: string;
}

export interface SingleOverwriteableElement extends OverwriteableElement {
	label: string; //confirm dialog
}

@Injectable({ providedIn: "root" })
export class EditService {
	private readonly viewService = inject(ViewService);
	private readonly dialogService = inject(DialogService);
	private readonly mutexService = inject(MutexService);
	private readonly processElementsResource = inject(ProcessElementsResource);
	private readonly modulesResource = inject(ModulesResource);

	async addChild(
		parent: ViewableElement | ViewableSourceElement,
		subtype: ViewableType | undefined,
		name: string,
		workspaceId: string,
		pv: string,
		dependent: boolean,
		skipDuplicationCheck: boolean = true,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Remove this comment and fix warnings!
	): Promise<any> {
		this.processElementsResource.setAdditionalHttpParamsForNextRequest({
			pv: pv,
			skipDuplicationCheck: skipDuplicationCheck.toString(),
		});
		const newChild = await this.processElementsResource.createChild(workspaceId, parent.type.ident, parent.id, {
			subtype: subtype ? subtype.subtypeIdent : undefined,
			name: name,
			dependent: dependent,
		});
		parent.children!.push(newChild as stages.process.ProcessTreeItem);
		return newChild;
	}

	async addSequence(
		origin: ViewableElement,
		predecessor: ViewableElement,
		names: string[],
		associationType: string,
		type: string,
		subtype: string,
		sourceRole: string,
		workspaceId: string,
		pv: string,
	): Promise<void> {
		const sequence = names.map((name) => {
			return {
				type: type,
				subtype: subtype,
				name: name,
				associationType: associationType,
				sourceRole: sourceRole,
			};
		});

		this.processElementsResource.setAdditionalHttpParamsForNextRequest({
			pv: pv,
		});
		return this.processElementsResource.addSequence(workspaceId, origin.type.ident, origin.id, sequence, {
			predecessorType: predecessor ? predecessor.type.ident : undefined,
			predecessorId: predecessor ? predecessor.id : undefined,
		});
	}

	async getEditableAttributes(
		elementTypeIdent: string,
		identity: string,
		attributeScope: AttributeScope,
		currentWorkspaceId: string,
		pv: string,
	): Promise<EditableAttribute[]> {
		return this.processElementsResource.getEditableAttributes(
			currentWorkspaceId,
			elementTypeIdent,
			identity,
			attributeScope,
			{
				pv: pv,
			},
		);
	}

	async saveAttributes(
		elementTypeIdent: string,
		identity: string,
		attributeScope: AttributeScope,
		attributes: Attribute[],
		currentWorkspaceId: string,
		pv: string,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Remove this comment and fix warnings!
	): Promise<any> {
		return this.processElementsResource.saveEditedAttributes(
			currentWorkspaceId,
			elementTypeIdent,
			identity,
			attributeScope,
			attributes,
			{
				pv: pv,
			},
		);
	}

	async getEditableElement(
		elementTypeIdent: string,
		identity: string,
		currentWorkspaceId: string,
		pv: string,
	): Promise<EditableElement> {
		return this.processElementsResource.getEditableElement(currentWorkspaceId, elementTypeIdent, identity, {
			pv: pv,
		});
	}

	async saveElement(
		elementTypeIdent: string,
		identity: string,
		element: stages.process.Element | stages.process.Process,
		currentWorkspaceId: string,
		pv: string,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Remove this comment and fix warnings!
	): Promise<any> {
		if (elementTypeIdent === "process") {
			return this.saveProcess(element as stages.process.Process, currentWorkspaceId, pv);
		}
		return this.processElementsResource.saveEditedElement(
			currentWorkspaceId,
			elementTypeIdent,
			identity,
			element as stages.process.Element,
			{
				pv: pv,
			},
		);
	}

	async getEditableProcess(currentWorkspaceId: string, pv: string): Promise<EditableProcess> {
		return this.processElementsResource.getEditableProcess(currentWorkspaceId, {
			pv: pv,
		});
	}

	async saveProcess(process: stages.process.Process, currentWorkspaceId: string, pv: string): Promise<void> {
		return this.processElementsResource.saveEditedProcess(currentWorkspaceId, process, {
			pv: pv,
		});
	}

	async renameElement(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Remove this comment and fix warnings!
		element: any,
		type: string,
		identity: string,
		elementName: string,
		currentWorkspaceId: string,
		pv: string,
	): Promise<void> {
		const updatedElement = await this.processElementsResource.renameProcessElement(currentWorkspaceId, type, identity, {
			name: elementName,
			pv: pv,
		});
		element.name = updatedElement.name;
		element.shortname = updatedElement.shortname;
		element.label = updatedElement.label;
		this.viewService.notifyModified();
	}

	async deleteSingleElement(
		workspaceId: string,
		pv: string,
		elementToDelete: SingleDeletableElement,
		afterDelete: () => void,
		onCancel: () => void,
	): Promise<void> {
		if (
			await this.dialogService.confirm(
				"delete.confirm.message",
				{ name: elementToDelete.label },
				"delete",
				"cancel",
				true,
			)
		) {
			this.mutexService.invoke("elementDelete", async () => {
				return this.deleteElements(workspaceId, pv, [elementToDelete]).then(afterDelete);
			});
		} else {
			onCancel();
		}
	}

	async deleteElements(workspaceId: string, pv: string, elements: DeletableElement[]): Promise<void> {
		const elementIds = elements.map((element) => {
			return {
				typeIdent: element.type.ident,
				id: element.id,
			};
		});
		this.processElementsResource.setAdditionalHttpParamsForNextRequest({
			pv: pv,
		});
		return this.processElementsResource.delete(workspaceId, elementIds);
	}

	async pasteElements(
		targetElement: TypedViewable,
		action: "copy" | "move",
		elements2Paste: TypedViewable[],
		workspaceId: string,
		pv: string,
	): Promise<void> {
		const elementIds = elements2Paste.map((element: TypedViewable) => {
			return {
				typeIdent: element.type.ident,
				id: element.id,
			};
		});
		this.processElementsResource.setAdditionalHttpParamsForNextRequest({
			pv: pv,
		});
		if (action === "copy") {
			return this.processElementsResource.copy(workspaceId, targetElement.type.ident, targetElement.id, elementIds);
		}
		return this.processElementsResource.move(workspaceId, targetElement.type.ident, targetElement.id, elementIds);
	}

	async pasteAssociations(
		sourceElement: TypedViewable,
		targetElement: TypedViewable,
		workspaceId: string,
		pv: string,
	): Promise<void> {
		if (sourceElement.type.ident !== targetElement.type.ident) {
			throw new Error(
				"element types must match; got '" + sourceElement.type.ident + "' and '" + targetElement.type.ident + "'",
			);
		}
		this.processElementsResource.setAdditionalHttpParamsForNextRequest({
			pv: pv,
		});
		return this.processElementsResource.copyAssociations(
			workspaceId,
			sourceElement.type.ident,
			sourceElement.id,
			targetElement.id,
		);
	}

	async rearrange(
		workspaceId: string,
		pv: string,
		folder: ViewableElement,
		alphabetical: boolean,
		sortedEntries: ViewableElement[],
	): Promise<void> {
		const sortedEntriesIds = sortedEntries.map((entry) => {
			return entry.id;
		});
		this.processElementsResource.setAdditionalHttpParamsForNextRequest({
			pv: pv,
		});
		return this.processElementsResource.rearrange(workspaceId, folder.type.ident, folder.id, sortedEntriesIds, {
			alphabetical: alphabetical,
		});
	}

	async overwriteSingleElement(workspaceId: string, pv: string, element: SingleOverwriteableElement): Promise<void> {
		return this.overwriteElements(workspaceId, pv, [element]);
	}

	async overwriteElements(workspaceId: string, pv: string, elements: OverwriteableElement[]): Promise<void> {
		const elementIds = elements.map((element) => {
			return {
				typeIdent: element.type.ident,
				id: element.id,
			};
		});
		return this.modulesResource.overwrite(workspaceId, elementIds, {
			pv: pv,
		});
	}

	async restoreSingleElement(
		workspaceId: string,
		pv: string,
		elementToRestore: SingleOverwriteableElement,
		afterRestore: () => void,
		onCancel: () => void,
	): Promise<void> {
		if (
			await this.dialogService.confirm(
				"overwrite.undo.confirm.message",
				{ name: elementToRestore.label },
				"process.element.overwrite.undo",
			)
		) {
			this.mutexService.invoke("elementUndoOverwrite", async () => {
				return this.restoreElements(workspaceId, pv, [elementToRestore]).then(afterRestore);
			});
		} else {
			onCancel();
		}
	}

	async restoreElements(workspaceId: string, pv: string, elements: OverwriteableElement[]): Promise<void> {
		const elementIds = elements.map((element) => {
			return {
				typeIdent: element.type.ident,
				id: element.id,
			};
		});

		return this.modulesResource.restore(workspaceId, elementIds, {
			pv: pv,
		});
	}
}
